import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Card from './Card';
import Table from './Table';
import Button from './Button';
import FormGroup from './FormGroup';
import Loading from './Loading';
import Alert from './Alert';
import EmptyState from './EmptyState';
import api from '../services/api';

function AdminConsole() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [editUserRole, setEditUserRole] = useState('user');
  const [submitting, setSubmitting] = useState(false);
  
  // File upload states
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getUsers(true); // Include inactive users
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      await api.createUser({
        email: newUserEmail,
        role: newUserRole
      });
      setSuccess('User created successfully!');
      setCreateModalOpen(false);
      setNewUserEmail('');
      setNewUserRole('user');
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      await api.updateUser(selectedUser.id, {
        role: editUserRole
      });
      setSuccess('User updated successfully!');
      setEditModalOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    setError(null);
    setSuccess(null);

    try {
      await api.updateUser(user.id, {
        is_active: !user.is_active
      });
      setSuccess(`User ${user.is_active ? 'deactivated' : 'activated'} successfully!`);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      await api.deleteUser(selectedUser.id);
      setSuccess('User deleted successfully!');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditUserRole(user.role);
    setEditModalOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const report = await api.uploadReport(selectedFile);
      setSuccess(`Report "${report.filename}" uploaded successfully! ${report.total_anomalies} anomalies imported.`);
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById('csv-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <Loading message="Loading users..." />;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
          User Management
        </Typography>
        <Button
          startIcon={<PersonAddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          Add User
        </Button>
      </Stack>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Report Upload Section */}
      <Card title="Import Report" gradient sx={{ mb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
            Upload a CSV report file to import anomalies into the system. The file should match the same format as reports imported via the API endpoint.
          </Typography>
          
          <Box>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="csv-file-input">
              <Button
                component="span"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={uploading}
                sx={{ mr: 2 }}
              >
                {selectedFile ? 'Change File' : 'Select CSV File'}
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" sx={{ color: '#e2e8f0', mt: 1, display: 'inline-block', ml: 2 }}>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
            )}
          </Box>

          {selectedFile && (
            <Button
              onClick={handleFileUpload}
              disabled={uploading}
              startIcon={<UploadFileIcon />}
            >
              {uploading ? 'Uploading...' : 'Upload Report'}
            </Button>
          )}
        </Stack>
      </Card>

      <Card title={`Users (${users.length})`} gradient>
        {users.length === 0 ? (
          <EmptyState message="No users found" />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Role</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Created At</strong></TableCell>
                  <TableCell><strong>Created By</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Typography sx={{ color: '#e2e8f0', fontWeight: 500 }}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.role === 'admin' ? <AdminPanelSettingsIcon /> : <PersonIcon />}
                        label={user.role.toUpperCase()}
                        size="small"
                        sx={{
                          background: user.role === 'admin'
                            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                            : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          background: user.is_active
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                        {new Date(user.created_at).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                        {user.created_by || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => openEditModal(user)}
                          sx={{ color: '#60a5fa' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleToggleActive(user)}
                          sx={{
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1
                          }}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(user)}
                          sx={{ color: '#ef4444' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      {/* Create User Modal */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <form onSubmit={handleCreateUser}>
          <DialogContent>
            <FormGroup label="Email Address" required>
              <TextField
                fullWidth
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@company.com"
                required
              />
            </FormGroup>

            <FormGroup label="Role" required>
              <TextField
                select
                fullWidth
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value)}
                required
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </FormGroup>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button variant="outlined" onClick={() => setCreateModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <form onSubmit={handleEditUser}>
          <DialogContent>
            <FormGroup label="Email">
              <TextField
                fullWidth
                value={selectedUser?.email || ''}
                disabled
              />
            </FormGroup>

            <FormGroup label="Role" required>
              <TextField
                select
                fullWidth
                value={editUserRole}
                onChange={(e) => setEditUserRole(e.target.value)}
                required
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </FormGroup>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button variant="outlined" onClick={() => setEditModalOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Updating...' : 'Update User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to permanently delete this user?
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            <strong>Email:</strong> {selectedUser?.email}
          </Typography>
          <Alert variant="warning">
            This action cannot be undone. The user will be permanently removed from the system.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            disabled={submitting}
            sx={{
              backgroundColor: '#ef4444',
              '&:hover': { backgroundColor: '#dc2626' }
            }}
          >
            {submitting ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminConsole;

